<template>
    <app-layout>
        <common-page-plain>
            <common-content-spacer height="medium">
                <common-page-header :heading="$t('pages.administrator.outreach_tasks.heading')"></common-page-header>

                <div class="outreach-tasks">
                    <div class="outreach-tasks__header">
                        <div>{{ $t('pages.administrator.outreach_tasks.table.task_id') }}</div>
                        <div>{{ $t('pages.administrator.outreach_tasks.table.pool_id') }}</div>
                        <div>{{ $t('pages.administrator.outreach_tasks.table.type') }}</div>
                        <div>{{ $t('pages.administrator.outreach_tasks.table.constrained_details') }}</div>
                        <div>{{ $t('pages.administrator.outreach_tasks.table.provider') }}</div>
                        <div>{{ $t('pages.administrator.outreach_tasks.table.npi') }}</div>
                        <div>{{ $t('pages.administrator.outreach_tasks.table.channel') }}</div>
                        <div>{{ $t('pages.administrator.outreach_tasks.table.status') }}</div>
                        <!-- <div>{{ $t('pages.administrator.outreach_tasks.table.actions') }}</div> -->
                    </div>

                    <div v-if="isLoading" class="outreach-tasks__loading">
                        <common-loader :isProcessing="true"></common-loader>
                    </div>

                    <div v-else-if="!items.length" class="outreach-tasks__empty">
                        {{ $t('pages.administrator.outreach_tasks.no_tasks') }}
                    </div>

                    <div v-else class="outreach-tasks__list">
                        <div v-for="item in items" :key="item.id" class="outreach-tasks__row">
                            <div>{{ item.id }}</div>
                            <div>
                                <router-link
                                    v-if="item.referralsPoolId"
                                    :to="{ name: 'referrals.made.pool.show', params: { id: item.referralsPoolId } }"
                                >
                                    {{ item.referralsPoolId }}
                                </router-link>
                                <span v-else>—</span>
                            </div>
                            <div>
                                <span
                                    :class="[
                                        'badge',
                                        getOutreachType(item) === 'constrained' ? 'badge--constrained' : 'badge--external',
                                    ]"
                                >
                                    {{ getOutreachTypeLabel(item) }}
                                </span>
                            </div>
                            <div class="outreach-tasks__details">
                                <span v-if="item.reasonCode" class="outreach-tasks__details__code">{{ item.reasonCode }}</span>
                                <span v-else>—</span>
                            </div>
                            <div>{{ item.providerName }}</div>
                            <div>{{ item.providerNpi || '—' }}</div>
                            <div>{{ item.primaryChannel || '—' }}</div>
                            <div>
                                <span
                                    :class="[
                                        'badge',
                                        item.status === 'PROCESSED' && 'badge--success',
                                        item.status === 'PROCESSING' && 'badge--warning',
                                        item.status === 'FAILED' && 'badge--danger',
                                        item.status === 'PENDING' && 'badge--muted',
                                    ]"
                                >
                                    {{ $t(`pages.administrator.outreach_tasks.status.${item.status}`) || item.status }}
                                </span>
                            </div>
                            <!-- <div>
                                <common-button
                                    :label="$t('common.view')"
                                    size="small"
                                    type="secondary"
                                    @click="viewDetails(item)"
                                ></common-button>
                                <common-button
                                    v-if="item.constrained || item.status === 'FAILED'"
                                    :label="$t('pages.administrator.outreach_tasks.table.resend')"
                                    size="small"
                                    type="primary"
                                    @click="resend(item)"
                                ></common-button>
                            </div> -->
                        </div>
                    </div>
                </div>
            </common-content-spacer>
        </common-page-plain>
        <common-modal v-if="showDetails" @close="showDetails = false">
            <div class="details-modal">
                <h3>{{ $t('pages.administrator.outreach_tasks.modal.title') }}</h3>
                <p>
                    <strong>{{ $t('pages.administrator.outreach_tasks.table.provider') }}:</strong>
                    {{ selected.providerName }}
                </p>
                <p>
                    <strong>{{ $t('pages.administrator.outreach_tasks.table.npi') }}:</strong>
                    {{ selected.providerNpi }}
                </p>
                <p>
                    <strong>{{ $t('pages.administrator.outreach_tasks.table.status') }}:</strong>
                    {{ $t(`pages.administrator.outreach_tasks.status.${selected.status}`) || selected.status }}
                </p>
                <p>
                    <strong>{{ $t('pages.administrator.outreach_tasks.table.type') }}:</strong>
                    {{ getOutreachTypeLabel(selected) }}
                </p>
                <p v-if="selected.reasonCode">
                    <strong>{{ $t('pages.administrator.outreach_tasks.table.error_code') }}:</strong>
                    {{ selected.reasonCode }}
                </p>
                <p v-if="selected.referralsPoolId">
                    <strong>Pool:</strong>
                    <router-link :to="{ name: 'referrals.sent.pool.show', params: { id: selected.referralsPoolId } }">
                        {{ selected.referralsPoolId }}
                    </router-link>
                </p>
            </div>
        </common-modal>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import AdministratorAPI from 'api/AdministratorAPI'

    export default {
        components: { AppLayout },
        data() {
            return {
                items: [],
                isLoading: false,
                showDetails: false,
                selected: {},
            }
        },
        methods: {
            getOutreachType(item) {
                if (!item) return 'external'
                return item.outreachType === 'constrained' || item.constrained ? 'constrained' : 'external'
            },
            getOutreachTypeLabel(item) {
                return this.getOutreachType(item) === 'constrained'
                    ? this.$t('pages.administrator.outreach_tasks.outreach_type.constrained')
                    : this.$t('pages.administrator.outreach_tasks.outreach_type.external')
            },
            load() {
                this.isLoading = true
                AdministratorAPI.getOutreachTasks({ limit: 100 })
                    .then((res) => {
                        const payload = res && res.data ? res.data : res
                        this.items = payload.items || []
                    })
                    .catch(() => {
                        this.items = []
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
            viewDetails(item) {
                this.selected = item
                this.showDetails = true
            },
            resend(item) {
                if (!confirm(this.$t('pages.administrator.outreach_tasks.confirm_resend'))) return
                AdministratorAPI.resendOutreachTask(item.id)
                    .then(() => {
                        this.load()
                    })
                    .catch(() => {
                        alert(this.$t('pages.administrator.outreach_tasks.resend_failed'))
                    })
            },
        },
        mounted() {
            this.load()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';

    .outreach-tasks__header {
        display: grid;
        grid-template-columns: 1fr 1fr minmax(220px, 3fr) 2fr 1fr 1fr 1fr 1fr;
        gap: 1rem;
        padding: 1rem;
        font-weight: bold;
        background: $color--bg-dropdown;
    }
    .outreach-tasks__row {
        display: grid;
        grid-template-columns: 1fr 1fr minmax(220px, 3fr) 2fr 1fr 1fr 1fr 1fr;
        gap: 1rem;
        padding: 0.75rem 1rem;
        border-bottom: 1px solid $color--border-base;
        align-items: start;
    }
    .badge {
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
    }
    .badge-warning {
        background: #f6c343;
        color: #333;
        margin-left: 8px;
    }
    .badge--success {
        background: #d4edda;
        color: #155724;
    }
    .badge--warning {
        background: #fff3cd;
        color: #856404;
    }
    .badge--danger {
        background: #f8d7da;
        color: #721c24;
    }
    .badge--muted {
        background: #e9ecef;
        color: #495057;
    }
    .badge--constrained {
        background: #fff3cd;
        color: #856404;
    }
    .badge--external {
        background: #cce5ff;
        color: #004085;
    }
    .outreach-tasks__details {
        font-size: 12px;
        color: $color--txt-minor;

        &__code {
            font-family: monospace;
            font-size: 11px;
            color: #666;
        }
    }
</style>
